import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { API_BASE } from "@/App";
import { useTrackSection } from "@/hooks/useTrackSection";
import {
  BookOpen, Target, TrendingUp, PenTool, Calendar,
  Lock, ArrowRight, Award, Sparkles, ArrowLeft, CheckCircle, Scale,
  FileText, Brain, Lightbulb, ChevronRight, Clock, Flame,
  GraduationCap, Trophy, Star, Play, Zap, BarChart3, Compass
} from "lucide-react";

/**
 * CLARIAS Mains Hub - SuperKalam Inspired Vibrant Design
 * Central hub for all Mains answer writing preparation
 */

const MainsHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  useTrackSection(); // Track section visit
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPro = user?.role === "PRO";
  const firstName = user?.name?.split(" ")[0] || "Aspirant";

  // Exam countdown - Mains September 2026
  const MAINS_DATE = new Date('2026-09-20');
  const getDaysRemaining = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exam = new Date(MAINS_DATE);
    exam.setHours(0, 0, 0, 0);
    return Math.ceil((exam - today) / (1000 * 60 * 60 * 24));
  };
  const daysToExam = getDaysRemaining();
  const weeksToExam = Math.floor(daysToExam / 7);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/mains/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  };

  // Track activity when clicking on features
  const handleFeatureClick = async (feature) => {
    try {
      const token = localStorage.getItem("token");
      await managedAxios.post(
        `${API_BASE}/command/track-activity?activity_type=mains&activity_name=${encodeURIComponent(feature.title)}&activity_path=${feature.path}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      // Silently fail - tracking is non-critical
    }
    navigate(feature.path);
  };

  // GS Suites - Hero features
  const gsSuites = [
    { 
      title: "GS-I Suite", 
      subtitle: "History, Geography, Society",
      description: "Indian Heritage, History, Geography, Society & Culture", 
      icon: BookOpen, 
      path: "/gs1-suite", 
      color: "from-orange-500 to-red-500",
      gradient: "from-orange-500/10 to-red-500/10",
      borderColor: "border-orange-200"
    },
    { 
      title: "GS-II Suite", 
      subtitle: "Polity, Governance, IR",
      description: "Constitution, Governance, Social Justice & International Relations", 
      icon: Scale, 
      path: "/gs2-suite", 
      color: "from-blue-500 to-indigo-500",
      gradient: "from-blue-500/10 to-indigo-500/10",
      borderColor: "border-blue-200"
    },
    { 
      title: "GS-III Suite", 
      subtitle: "Economy, Environment, Tech",
      description: "Economy, Environment, Security & Technology", 
      icon: TrendingUp, 
      path: "/gs3-suite", 
      color: "from-emerald-500 to-teal-500",
      gradient: "from-emerald-500/10 to-teal-500/10",
      borderColor: "border-emerald-200"
    },
    { 
      title: "GS-IV Suite", 
      subtitle: "Ethics & Integrity",
      description: "Ethics, Integrity, Aptitude & Case Studies", 
      icon: Award, 
      path: "/gs4-suite", 
      color: "from-purple-500 to-pink-500",
      gradient: "from-purple-500/10 to-pink-500/10",
      borderColor: "border-purple-200"
    },
  ];

  // Special features
  const specialFeatures = [
    { 
      title: "Essay Suite", 
      description: "AI-predicted topics, writing & evaluation", 
      icon: PenTool, 
      path: "/essay-suite", 
      color: "from-pink-500 to-rose-500",
      stats: "10 predictions"
    },
    { 
      title: "Test Series", 
      description: "Full-length mock tests with rubric scoring", 
      icon: Target, 
      path: "/tests", 
      color: "from-amber-500 to-orange-500",
      stats: "Examiner-grade"
    },
  ];

  // Tools
  const toolFeatures = [
    { title: "My Evaluations", description: "Revisit every evaluated answer & feedback", icon: FileText, path: "/my-evaluations", color: "from-indigo-500 to-blue-600" },
    { title: "Weakness Map", description: "Your blind spots & improvement trends", icon: Compass, path: "/weakness-map", color: "from-rose-500 to-pink-600" },
    { title: "Revision Scheduler", description: "Track your mains revision", icon: Calendar, path: "/mains-revision", color: "from-violet-500 to-purple-500" },
    { title: "Progress Report", description: "Your mains preparation analytics", icon: BarChart3, path: "/progress-report", color: "from-cyan-500 to-teal-500" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-purple-300">Loading Mains Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-pink-50/30">
      
      {/* ═══════════════════════════════════════════════════════════
          VIBRANT HEADER
          ═══════════════════════════════════════════════════════════ */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          {/* Back button & Title */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate("/dashboard")}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <PenTool className="w-7 h-7" />
                  Mains Hub
                </h1>
                <p className="text-purple-200 text-sm">Master answer writing</p>
              </div>
            </div>
            
            {/* Days Counter */}
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2">
              <p className="text-2xl font-bold leading-none">{daysToExam}</p>
              <p className="text-[10px] text-purple-200">days to Mains</p>
            </div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.total_answers || 0}</p>
              <p className="text-[10px] text-purple-200">Answers</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.avg_score || 0}</p>
              <p className="text-[10px] text-purple-200">Avg Score</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{stats?.essays_written || 0}</p>
              <p className="text-[10px] text-purple-200">Essays</p>
            </div>
            <div className="bg-gradient-to-br from-amber-500/30 to-orange-500/30 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-xl font-bold">{weeksToExam}</p>
              <p className="text-[10px] text-amber-200">Weeks Left</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        
        {/* ═══════════════════════════════════════════════════════════
            TOPPER TIP BANNER
            ═══════════════════════════════════════════════════════════ */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-purple-100 to-pink-100 overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-purple-900">Topper's Secret</p>
                <p className="text-sm text-purple-700">"Write 1 answer daily. Quality beats quantity. Get it evaluated."</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════
            GS SUITES - HERO GRID
            ═══════════════════════════════════════════════════════════ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-purple-500" />
            <h2 className="font-bold font-heading text-lg text-slate-800">GS Answer Writing Suites</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {gsSuites.map((suite, idx) => (
              <Card 
                key={idx}
                className={`border-2 ${suite.borderColor} bg-gradient-to-br ${suite.gradient} cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all group overflow-hidden`}
                onClick={() => handleFeatureClick(suite)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${suite.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg`}>
                      <suite.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold font-heading text-lg text-slate-800">{suite.title}</h3>
                      <p className="text-xs text-slate-500 mb-1">{suite.subtitle}</p>
                      <p className="text-sm text-slate-600">{suite.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200/50">
                    <Badge variant="outline" className="text-xs">
                      {isPro ? "Unlimited answers" : "1 free answer"}
                    </Badge>
                    <div className="flex items-center gap-1 text-sm font-medium text-slate-600">
                      Start Writing
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            SPECIAL FEATURES
            ═══════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-2 gap-4">
          {specialFeatures.map((feature, idx) => (
            <Card 
              key={idx}
              className={`border-0 shadow-xl cursor-pointer hover:scale-[1.02] transition-all overflow-hidden bg-gradient-to-br ${feature.color}`}
              onClick={() => handleFeatureClick(feature)}
            >
              <CardContent className="p-5 text-white">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <feature.icon className="w-6 h-6" />
                    </div>
                    {feature.proOnly && !isPro && (
                      <Badge className="bg-white/20 text-white border-0">
                        <Lock className="w-3 h-3 mr-1" />
                        PRO
                      </Badge>
                    )}
                  </div>
                  <h3 className="font-bold font-heading text-lg mb-1">{feature.title}</h3>
                  <p className="text-white/80 text-sm mb-3 flex-1">{feature.description}</p>
                  <div className="flex items-center justify-between">
                    <Badge className="bg-white/20 text-white border-0 text-xs">
                      {feature.stats}
                    </Badge>
                    <ChevronRight className="w-5 h-5 text-white/60" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ═══════════════════════════════════════════════════════════
            TOOLS
            ═══════════════════════════════════════════════════════════ */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-indigo-500" />
            <h2 className="font-bold font-heading text-lg text-slate-800">Tools & Analytics</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {toolFeatures.map((feature, idx) => (
              <Card 
                key={idx}
                className="border border-slate-200 bg-white cursor-pointer hover:shadow-lg hover:border-transparent transition-all group overflow-hidden"
                onClick={() => handleFeatureClick(feature)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform shadow-md`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium text-sm text-slate-800">{feature.title}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            WRITING STREAK / PROGRESS
            ═══════════════════════════════════════════════════════════ */}
        <Card className="border border-slate-200 bg-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-500" />
                <span className="font-semibold text-slate-800">This Week's Progress</span>
              </div>
              <Badge variant="outline" className="text-xs">
                {stats?.answers_this_week || 0} answers
              </Badge>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, idx) => {
                // Check if this day (0=Mon, 6=Sun) is in days_active array
                const isActive = stats?.days_active?.includes(idx) || false;
                return (
                  <div key={idx} className="text-center">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-1 ${
                      isActive 
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white' 
                        : 'bg-slate-100 text-slate-400'
                    }`}>
                      {isActive ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <span className="text-xs font-medium">{day}</span>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500">{day}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* ═══════════════════════════════════════════════════════════
            PRO UPGRADE CTA (for FREE users)
            ═══════════════════════════════════════════════════════════ */}
        {!isPro && (
          <Card 
            className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate("/upgrade")}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold font-heading text-lg text-slate-800">Upgrade to CLARIAS PRO</p>
                  <p className="text-sm text-slate-600">Unlock unlimited GS answers, essay suite & more</p>
                </div>
                <ChevronRight className="w-6 h-6 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Spacing */}
        <div className="h-8" />
      </div>
    </div>
  );
};

export default MainsHubPage;
